<template>
  <div class="w-full h-full">
    <div class="pt-10">
      <ErrorDisplay v-if="displayErrorMessage" :errorMessage="errorMessage" />
      <div v-else class="text-center mb-4">
        <div class="text-center">
          <div class="my-3">
            <LayupIcon class="my-3" />
            <h5 class="h5 font-semibold text-gray-light-700 text-lg">Verify your {{ title }}</h5>
            <p v-if="generalStore.passwordReset">
              If your number is registered, you will receive an SMS with a verification code.
            </p>
            <p class="text-sm" v-else>We've sent a code to {{ value }}</p>
            <p class="text-sm text-green-500" v-if="isOtpSentSuccessfully">OTP resent successfully.</p>
            <p v-if="errorMessage" class="text-error-700">{{ errorMessage }}</p>
          </div>
        </div>

        <div v-if="isLoading">
          <LayupSpinner />
          <p class="text-center">Verifying {{ title }}...</p>
        </div>
        <div class="text-center" v-else>
          <OtpInput v-model="digitInputs" />
          <p v-if="turnstileToken" class="mt-3 text-sm">
            Didn't get a code?
            <a class="text-brand-700 p-0 text-sm cursor-pointer" @click="resendOtp()">Click to resend</a>.
          </p>
          <TurnstileWidget ref="turnstileRef" @success="handleSuccess" />
        </div>
      </div>

      <button
        type="button"
        class="w-full border rounded h-10 w-100 mt-3 disabled:opacity-50 hover:bg-gray-500 hover:text-white"
        @click="router.back()"
        :disabled="isLoading"
      >
        Back
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import ErrorDisplay from '@/components/common/ErrorDisplay.vue';
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import TurnstileWidget from '@/components/common/TurnstileWidget.vue';
import LayupIcon from '@/components/icons/LayupIcon.vue';
import { NetworkError } from '@/errors';
import { forgotPassword, otpVerification } from '@/services/auth';
import { useGeneralStore } from '@/stores/general';
import { useUserStore } from '@/stores/users';
import { useWidgetStore } from '@/stores/widget';
import { maskEmail } from '@/utils/form.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { onMounted, ref, toRaw, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import OtpInput from '../../common/OtpInput.vue';

const userStore = useUserStore();
const generalStore = useGeneralStore();
const widgetStore = useWidgetStore();
const route = useRoute();
const router = useRouter();
const turnstileToken = ref('');
const otpValue = ref('');
const errorMessage = ref('');
const displayErrorMessage = !userStore.user.cellNumber && !userStore.user.email;
const title = ref('');
const isLoading = ref(false);
const isOtpSentSuccessfully = ref(false);
const cellNumber = toRaw(userStore.user.cellNumber!);
const email = toRaw(userStore.user.email);
const digitInputs = ref(['', '', '', '', '']);
const value = ref('');
const isEmailVerificationNeeded = userStore.user.verifiedMobile && email && !userStore.user.verifiedEmail;
const turnstileRef = ref<InstanceType<typeof TurnstileWidget> | null>(null);

const init = () => {
  value.value = isEmailVerificationNeeded ? maskEmail(email) : cellNumber;
  title.value = isEmailVerificationNeeded ? 'email' : 'mobile number';
};

onMounted(() => {
  if (displayErrorMessage) {
    errorMessage.value = 'Your session has expired. Please go back and try again.';
    createWarning(new Error(errorMessage.value));
  }
});

watch(otpValue, async (newOtp) => {
  isOtpSentSuccessfully.value = false;
  if (newOtp.length === 5) {
    await handleOtp();
  }
});

watch(digitInputs, async (newOtp) => {
  otpValue.value = newOtp.join('');
});

const handleOtp = async () => {
  errorMessage.value = '';
  isLoading.value = true;
  if (otpValue.value.length < 5) {
    errorMessage.value = 'Invalid otp';
  } else {
    try {
      let channel: 'MOBILE' | 'EMAIL';
      let channelId: string;
      if (isEmailVerificationNeeded) {
        channel = 'EMAIL';
        channelId = email;
      } else {
        channel = 'MOBILE';
        channelId = cellNumber;
      }
      const response = await otpVerification(otpValue.value, channel, channelId);
      userStore.user = response;

      addMetric('otp_verification', { status: 'success', channel: 'widget' });
      const goTo =
        widgetStore.order.type === 'REWARDS' && !userStore.user.verifiedEmail ? 'EnterEmail' : 'WidgetSignIn';
      if (generalStore.passwordReset) {
        generalStore.passwordReset = false;
        await router.replace({ name: 'CreatePassword' });
      } else {
        await router.replace({ name: goTo });
      }
    } catch (error: any) {
      if (error instanceof NetworkError) {
        createWarning(error);
        addMetric('otp_verification', { status: 'network_error', channel: 'widget' });
        errorMessage.value = 'Network Error. Please try again.';
      } else {
        createException(error);
        addMetric('otp_verification', { status: 'error', channel: 'widget' });
        if (error?.response?.data) {
          errorMessage.value = error.response.data;
        } else {
          errorMessage.value = 'An unexpected error occurred. Please try again.';
        }
      }
    } finally {
      isLoading.value = false;
    }
  }
};

const resendOtp = async () => {
  try {
    isOtpSentSuccessfully.value = false;
    digitInputs.value = ['', '', '', '', ''];
    errorMessage.value = '';

    const { redirectTo } = route.query as { redirectTo?: string };
    const { user } = userStore;
    const isRewardsOrder = widgetStore.order.type === 'REWARDS';
    const isRealtimeVerification = isRewardsOrder && !user.verifiedEmail;

    let channel: 'MOBILE' | 'EMAIL';
    let channelId: string;
    if (isRewardsOrder && !user.verifiedEmail) {
      channel = 'EMAIL';
      channelId = email!;
    } else {
      channel = 'MOBILE';
      channelId = cellNumber!;
    }
    await forgotPassword(redirectTo!, channelId, channel, turnstileToken.value, isRealtimeVerification);
    addMetric('resend_otp', { status: 'success', channel: 'widget' });
    isOtpSentSuccessfully.value = true;
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('resend_otp', { status: 'network_error', channel: 'widget' });
      errorMessage.value = 'Network Error. Please try again.';
    } else {
      createException(error);
      addMetric('resend_otp', { status: 'error', channel: 'widget' });
      if (error?.response?.data) {
        errorMessage.value = error.response.data;
      } else {
        errorMessage.value = 'An unexpected error occurred. Please try again.';
      }
    }
  } finally {
    turnstileToken.value = '';
    turnstileRef.value?.reset();
  }
};

function handleSuccess(token: string) {
  turnstileToken.value = token;
}

init();
</script>
